import type { FC, ReactNode } from 'react'
import type { ArticleSummary, ExperimentSummary, ProjectSummary } from '@/types'

import Link from 'next/link'
import { ArrowRight } from 'lucide-react'
import { formatDate } from '@/lib/format'
import MainContainer from '@/components/generals/main-container'
import EditorialGrid from '@/components/generals/editorial-grid'
import Button from '@/components/elements/button'
import ExperimentStatus from '@/components/lab/experiment-status'
import { siteConfig } from '@/constants/site'

interface HomeHeroProps {
    project: ProjectSummary | null
    experiment: ExperimentSummary | null
    article: ArticleSummary | null
}

interface IndexEntryProps {
    number: string
    section: string
    href: string
    title: string
    meta: ReactNode
}

const IndexEntry: FC<IndexEntryProps> = ({ number, section, href, title, meta }): ReactNode => (
    <li className='border-b border-border'>
        <Link href={href} className='group grid grid-cols-[2.25rem_1fr_auto] items-start gap-x-3 py-5'>
            <span className='pt-1 font-mono text-caption text-muted-foreground'>{number}</span>

            <span className='flex min-w-0 flex-col gap-1.5'>
                <span className='font-mono text-label uppercase text-muted-foreground'>{section}</span>
                <span className='font-heading text-subheading [font-weight:500] transition-colors duration-200 group-hover:text-brand'>
                    {title}
                </span>
                <span className='font-mono text-caption text-muted-foreground'>{meta}</span>
            </span>

            <ArrowRight
                aria-hidden='true'
                className='mt-1 size-4 text-muted-foreground transition-[transform,color] duration-300 ease-editorial group-hover:translate-x-1 group-hover:text-brand'
            />
        </Link>
    </li>
)

/** The front page: a cover-style headline with an "in this issue" index of the latest work. */
const HomeHero: FC<HomeHeroProps> = ({ project, experiment, article }): ReactNode => {
    return (
        <section id='hero' aria-labelledby='hero-title'>
            <MainContainer className='pt-10 pb-20 md:pt-16 md:pb-28'>
                <div className='flex items-center justify-between gap-6 border-b border-border pb-4'>
                    <p className='flex items-center gap-2.5 font-mono text-label uppercase text-muted-foreground'>
                        <span aria-hidden='true' className='size-1.5 bg-brand' />
                        Software engineering / AI &amp; automation
                    </p>

                    <p className='hidden font-mono text-label uppercase text-muted-foreground md:block'>
                        Portfolio / Lab / Journal
                    </p>
                </div>

                <h1
                    id='hero-title'
                    className='mt-12 max-w-[16ch] font-heading text-display [font-weight:380] text-foreground animate-in fade-in slide-in-from-bottom-4 duration-1000 ease-out md:mt-16'
                >
                    I build <em className='text-brand'>calm</em>, useful software.
                </h1>

                <EditorialGrid className='mt-12 gap-y-14 md:mt-16'>
                    <div className='col-span-4 flex flex-col gap-8 animate-in fade-in slide-in-from-bottom-2 delay-200 duration-1000 ease-out fill-mode-backwards md:col-span-6 lg:col-span-5'>
                        <p className='max-w-[34ch] text-lede text-foreground'>
                            {siteConfig.supportingTagline} Exploring the space between software, systems, and ideas.
                        </p>

                        <div className='flex flex-wrap items-center gap-x-8 gap-y-4'>
                            <Button href='/projects' size='lg' icon='arrow'>
                                Explore the work
                            </Button>

                            <Button href='/journal' size='lg' variant='quiet'>
                                Read the journal
                            </Button>
                        </div>
                    </div>

                    <aside
                        aria-labelledby='in-this-issue'
                        className='col-span-4 animate-in fade-in delay-300 duration-1000 ease-out fill-mode-backwards md:col-span-6 lg:col-span-5 lg:col-start-8'
                    >
                        <p id='in-this-issue' className='font-mono text-label uppercase text-foreground'>
                            In this issue
                        </p>

                        <ol className='mt-4 border-t border-foreground'>
                            {project && (
                                <IndexEntry
                                    number='01'
                                    section='Work'
                                    href={`/projects/${project.slug}`}
                                    title={project.title}
                                    meta={`${project.categoryLabel} / ${project.year}`}
                                />
                            )}

                            {experiment && (
                                <IndexEntry
                                    number='02'
                                    section='AI Lab'
                                    href={`/lab/${experiment.slug}`}
                                    title={experiment.title}
                                    meta={<ExperimentStatus status={experiment.status} className='text-caption' />}
                                />
                            )}

                            {article && (
                                <IndexEntry
                                    number='03'
                                    section='Journal'
                                    href={`/journal/${article.slug}`}
                                    title={article.title}
                                    meta={`${formatDate(article.date)} / ${article.readingTime} min read`}
                                />
                            )}
                        </ol>
                    </aside>
                </EditorialGrid>
            </MainContainer>
        </section>
    )
}

export default HomeHero
